import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {  Home, Mail, FileText, GraduationCap, MessageSquare,
  Star, Lightbulb, Handshake, Settings, LogOut,
  ChevronRight, Sun, Moon, UserPlus,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

import { API_URL } from '../config/api';

function cn(...classes) {
  return classes.filter(Boolean).join(' ');
}

const Badge = ({ count, active }) => (
  <span className={cn('sb-badge', active && 'sb-badge--active')}>{count}</span>
);

const NavItem = ({ item, active, collapsed, onClick }) => {
  const [hovered, setHovered] = useState(false);
  const Icon = item.icon;
  return (
    <div
      className={cn('sb-nav-item', active && 'sb-nav-item--active', hovered && !active && 'sb-nav-item--hover')}
      onClick={() => onClick(item.path)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div className={cn('sb-bar', active && 'sb-bar--active', hovered && !active && 'sb-bar--hover')} />
      <div className={cn('sb-icon-shell', active && 'sb-icon-shell--active', hovered && !active && 'sb-icon-shell--hover')}>
        <Icon size={18} strokeWidth={active ? 2.2 : 1.8} />
        {active && <span className="sb-pulse" />}
      </div>
      {!collapsed && <span className={cn('sb-label', active && 'sb-label--active')}>{item.label}</span>}
      {!collapsed && (
        <div className="sb-right">
          {item.badge && <Badge count={item.badge} active={active} />}
          {active && <ChevronRight size={13} className="sb-chevron" />}
        </div>
      )}
      {collapsed && item.badge && <Badge count={item.badge} active={active} />}
    </div>
  );
};

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { theme, toggleTheme } = useTheme();
  const { user, logout } = useAuth();
  const collapsed = !sidebarOpen;
  const isDark    = theme === 'dark';
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // ─── Données utilisateur ──────────────────────────────────────────────────
  const token = localStorage.getItem('admin_token') || '';
  const isSuperAdmin = user?.role === 'super_admin';

  // ─── Déconnexion ──────────────────────────────────────────────────────────
  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      await axios.post(
        `${API_URL}/logout`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (_) {
      // On déconnecte quand même si l'API échoue
    } finally {
      localStorage.removeItem('welcome_shown');
      logout(); // Utiliser la fonction logout du contexte
      toast.info('Vous avez été déconnecté.', {
        position: 'top-right',
        autoClose: 2000,
        style: { borderRadius: '12px', fontWeight: '600' },
      });
      setTimeout(() => navigate('/login'), 800);
    }
  };

  const getActiveMenu = () => {
    const path = location.pathname;
    if (path === '/admin/dashboard' || path === '/admin/dashboard/') return 'dashboard';
    if (path.includes('/contactdashboard')) return 'contact';
    if (path.includes('/newsletter'))       return 'newsletter';
    if (path.includes('/devis'))            return 'devis';
    if (path.includes('/inscriptions'))     return 'inscriptions';
    if (path.includes('/avis-chaud'))       return 'avis-chaud';
    if (path.includes('/suggestions'))      return 'suggestions';
    if (path.includes('/partenariats'))     return 'partenariats';
    if (path.includes('/settings'))         return 'settings';
    if (path.includes('/formations'))       return 'formations';
    if (path.includes('/admin'))            return 'createadmin';
    return 'dashboard';
  };

  const activeMenu = getActiveMenu();

  const allMenuItems = [
    { id: 'dashboard',    label: 'Dashboard',      icon: Home,          path: '/admin/dashboard' },
    { id: 'newsletter',   label: 'Newsletter',     icon: Mail,           path: '/newsletter' },
    { id: 'devis',        label: 'Devis',          icon: FileText,    path: '/devis' },
    { id: 'inscriptions', label: 'Inscriptions',   icon: GraduationCap, path: '/inscriptions' },
    { id: 'contact',      label: 'Contact',        icon: MessageSquare, path: '/contactdashboard' },
    { id: 'avis-chaud',   label: 'Avis à chaud',  icon: Star,          path: '/avis-chaud' },
    { id: 'suggestions',  label: 'Recueil des attentes',    icon: Lightbulb,     path: '/suggestions' },
    { id: 'partenariats', label: 'Partenariats',   icon: Handshake,     path: '/partenariats' },
    {id: 'formations',    label: 'Formations',     icon: GraduationCap, path: '/formations' },
    // Visible uniquement pour le super_admin
    ...(isSuperAdmin ? [{ id: 'createadmin', label: 'Création Admin', icon: UserPlus, path: '/admin' }] : []),
    { id: 'settings',     label: 'Paramètres',     icon: Settings,      path: '/settings' },
  ];

  const handleMenuClick = (path) => {
    navigate(path);
    if (window.innerWidth <= 1024) setSidebarOpen(false);
  };

  const colors = {
    bg:        isDark ? 'rgba(11, 14, 24, 0.95)' : 'rgba(255, 255, 255, 0.95)',
    border:    isDark ? 'rgba(255,255,255,0.08)'  : 'rgba(0,0,0,0.08)',
    text:      isDark ? '#c8d4e0'                  : '#1e2d3d',
    textDim:   isDark ? '#5a6b82'                  : '#8fa3b5',
    textLabel: isDark ? '#3d5060'                  : '#b0bec8',
    surface:   isDark ? 'rgba(255,255,255,0.05)'   : 'rgba(0,0,0,0.04)',
    logoFilter:isDark ? 'brightness(0) invert(1) opacity(0.9)' : 'none',
    shadow:    isDark ? '0 24px 64px rgba(0,0,0,0.7)' : '0 12px 40px rgba(0,0,0,0.1)',
  };

  return (
    <>
      <style>{`
        html { transition: background 0.3s; }
        html[data-theme="dark"]  body { background: #090c16; color: #c8d4e0; }
        html[data-theme="light"] body { background: #f0f3f8; color: #1e2d3d; }
      `}</style>

      <aside className={cn('sb', collapsed && 'sb--collapsed')} style={{ background: colors.bg, borderColor: colors.border, boxShadow: colors.shadow }}>
        <div className="sb-halo sb-halo-1" />
        <div className="sb-halo sb-halo-2" />
        <div className="sb-halo sb-halo-3" />
        <div className="sb-grid" style={{ borderColor: colors.border }} />

        <div className="sb-header" style={{ borderBottomColor: colors.border }}>
          <div className="sb-logo-wrap" onClick={() => setSidebarOpen(!sidebarOpen)} title="Réduire / Agrandir">
            <img
              src="../assets/images/Logo_CINEF.png"
              alt="Cinef Formation"
              className={cn('sb-logo', collapsed && 'sb-logo--mini')}
              style={{ filter: colors.logoFilter }}
            />
          </div>
        </div>

        <div className="sb-divider" />

        <nav className="sb-nav">
          {!collapsed && <p className="sb-section" style={{ color: colors.textLabel }}></p>}
          {allMenuItems.map((item) => (
            <NavItem key={item.id} item={item} active={activeMenu === item.id} collapsed={collapsed} onClick={handleMenuClick} />
          ))}
        </nav>

        <div className="sb-footer" style={{ borderTopColor: colors.border }}>
          <button className="sb-theme-btn" onClick={toggleTheme} style={{ borderColor: colors.border, background: colors.surface }}>
            <div className="sb-icon-shell" style={{ color: colors.textDim, background: colors.surface, borderColor: 'transparent' }}>
              {isDark ? <Sun size={16} strokeWidth={1.8} /> : <Moon size={16} strokeWidth={1.8} />}
            </div>
            {!collapsed && (
              <>
                <span className="sb-label" style={{ color: colors.textDim, fontWeight: 500, fontSize: 15 }}>
                  {isDark ? 'Mode clair' : 'Mode sombre'}
                </span>
                <div className={cn('sb-pill', isDark && 'sb-pill--on')}>
                  <div className="sb-pill-thumb" />
                </div>
              </>
            )}
          </button>

          <div className={cn('sb-logout', isLoggingOut && 'sb-logout--loading')} onClick={handleLogout} style={{ borderColor: colors.border, background: colors.surface }}>
            <div className="sb-icon-shell sb-icon-shell--logout" style={{ color: colors.textDim, background: 'transparent', borderColor: 'transparent' }}>
              {isLoggingOut ? (
                <svg className="sb-spinner" viewBox="0 0 24 24" fill="none" width="17" height="17">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.3"/>
                  <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
                </svg>
              ) : (
                <LogOut size={17} strokeWidth={1.8} />
              )}
            </div>
            {!collapsed && (
              <span className="sb-label" style={{ color: colors.textDim, fontWeight: 500, fontSize: 15 }}>
                {isLoggingOut ? 'Déconnexion...' : 'Déconnexion'}
              </span>
            )}
          </div>
        </div>
      </aside>

      {sidebarOpen && window.innerWidth <= 1024 && (
        <div className="sb-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&display=swap');
        .sb { position: fixed; left: 12px; top: 12px; bottom: 12px; width: 252px; border: 1px solid; border-radius: 18px; display: flex; flex-direction: column; z-index: 1000; overflow: hidden; transition: width 0.28s cubic-bezier(0.4,0,0.2,1); font-family: 'Plus Jakarta Sans', sans-serif; backdrop-filter: blur(24px) saturate(150%); -webkit-backdrop-filter: blur(24px) saturate(150%); }
        .sb--collapsed { width: 74px; }
        .sb-halo { position: absolute; border-radius: 50%; pointer-events: none; z-index: 0; filter: blur(40px); }
        .sb-halo-1 { width: 140px; height: 130px; top: -30px; left: -30px; background: radial-gradient(circle, rgba(253,126,20,0.2) 0%, transparent 70%); }
        .sb-halo-2 { width: 110px; height: 160px; bottom: 60px; right: -40px; background: radial-gradient(circle, rgba(25,135,84,0.16) 0%, transparent 70%); }
        .sb-halo-3 { width: 100px; height: 100px; bottom: -20px; left: 10px; background: radial-gradient(circle, rgba(32,201,151,0.12) 0%, transparent 70%); }
        .sb-grid { position: absolute; inset: 0; pointer-events: none; z-index: 0; opacity: 0.3; background-image: linear-gradient(rgba(128,128,128,0.15) 1px, transparent 1px), linear-gradient(90deg, rgba(128,128,128,0.15) 1px, transparent 1px); background-size: 28px 28px; mask-image: linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.5) 20%, rgba(0,0,0,0.5) 80%, transparent 100%); }
        .sb > *:not(.sb-halo):not(.sb-grid) { position: relative; z-index: 1; }
        .sb-header { padding: 18px 14px 14px; display: flex; align-items: center; justify-content: center; gap: 8px; border-bottom: 1px solid transparent; flex-shrink: 0; }
        .sb-logo-wrap { cursor: pointer; display: flex; align-items: center; justify-content: center; flex: 1; min-width: 0; transition: opacity 0.2s; }
        .sb-logo-wrap:hover { opacity: 0.72; }
        .sb-logo { max-width: 110px; height: auto; object-fit: contain; transition: all 0.28s ease; display: block; }
        .sb-logo--mini { max-width: 32px; height: 32px; }
        .sb-divider { height: 1px; margin: 0 12px; flex-shrink: 0; background: linear-gradient(90deg, transparent 0%, rgba(253,126,20,0.3) 40%, rgba(25,135,84,0.2) 70%, transparent 100%); }
        .sb-nav { flex: 1; overflow-y: auto; overflow-x: hidden; padding: 10px 8px 6px; display: flex; flex-direction: column; gap: 2px; scrollbar-width: none; min-height: 0; }
        .sb-nav::-webkit-scrollbar { display: none; }
        .sb-section { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 9px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; padding: 2px 8px 8px; margin: 0; white-space: nowrap; flex-shrink: 0; }
        .sb-nav-item { position: relative; display: flex; align-items: center; gap: 10px; padding: 10px 10px 10px 14px; border-radius: 11px; cursor: pointer; transition: all 0.2s ease; border: 1px solid transparent; flex-shrink: 0; min-height: 46px; }
        .sb-nav-item--hover { background: rgba(253,126,20,0.07); border-color: rgba(253,126,20,0.14); }
        .sb-nav-item--active { background: rgba(25,135,84,0.1); border-color: rgba(25,135,84,0.22); box-shadow: 0 2px 14px rgba(25,135,84,0.1); }
        .sb-bar { position: absolute; left: 0; top: 50%; transform: translateY(-50%); width: 3px; height: 0; border-radius: 0 3px 3px 0; transition: height 0.25s cubic-bezier(0.34,1.56,0.64,1); }
        .sb-bar--hover { height: 18px; background: linear-gradient(180deg, #fd7e14 0%, #dc3545 100%); box-shadow: 0 0 8px rgba(253,126,20,0.4); }
        .sb-bar--active { height: 28px; background: linear-gradient(180deg, #198754 0%, #20c997 100%); box-shadow: 0 0 10px rgba(25,135,84,0.4); }
        .sb-icon-shell { width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; position: relative; border: 1px solid transparent; transition: all 0.2s ease; }
        .sb-icon-shell--hover { color: #fd7e14 !important; background: rgba(253,126,20,0.12) !important; border-color: rgba(253,126,20,0.22) !important; }
        .sb-nav-item--hover .sb-icon-shell { color: #fd7e14; background: rgba(253,126,20,0.12); border-color: rgba(253,126,20,0.22); }
        .sb-icon-shell--active { color: #198754 !important; background: rgba(25,135,84,0.12) !important; border-color: rgba(25,135,84,0.25) !important; box-shadow: 0 0 12px rgba(25,135,84,0.2); }
        .sb-pulse { position: absolute; inset: -4px; border-radius: 13px; border: 1px solid rgba(32,201,151,0.35); animation: sb-pulse-anim 2.5s ease-out infinite; pointer-events: none; }
        @keyframes sb-pulse-anim { 0% { opacity: 0.9; transform: scale(1); } 60% { opacity: 0; transform: scale(1.3); } 100% { opacity: 0; } }
        .sb-label { font-family: 'Plus Jakarta Sans', sans-serif; font-size: 16px; font-weight: 500; white-space: nowrap; flex: 1; letter-spacing: 0.1px; transition: color 0.2s; }
        .sb-label--active { color: #198754 !important; font-weight: 700; }
        .sb-nav-item--hover .sb-label { color: #fd7e14 !important; }
        .sb-right { display: flex; align-items: center; gap: 5px; margin-left: auto; flex-shrink: 0; }
        .sb-chevron { color: #20c997; opacity: 0.8; }
        .sb-badge { min-width: 20px; height: 20px; border-radius: 10px; padding: 0 6px; font-size: 10px; font-weight: 700; font-family: 'Plus Jakarta Sans', sans-serif; display: flex; align-items: center; justify-content: center; background: rgba(220,53,69,0.12); color: #dc3545; border: 1px solid rgba(220,53,69,0.22); white-space: nowrap; flex-shrink: 0; }
        .sb-badge--active { background: rgba(25,135,84,0.12); color: #20c997; border-color: rgba(32,201,151,0.25); }
        .sb--collapsed .sb-badge { position: absolute; top: 5px; right: 5px; min-width: 16px; height: 16px; font-size: 8px; padding: 0 4px; }
        .sb-footer { padding: 10px 8px 12px; display: flex; flex-direction: column; gap: 5px; border-top: 1px solid transparent; flex-shrink: 0; }
        .sb-theme-btn { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 11px; border: 1px solid; cursor: pointer; width: 100%; text-align: left; transition: all 0.2s; font-family: 'Plus Jakarta Sans', sans-serif; min-height: 44px; }
        .sb-theme-btn:hover { background: rgba(253,126,20,0.08) !important; border-color: rgba(253,126,20,0.25) !important; }
        .sb-pill { width: 32px; height: 18px; border-radius: 9px; background: rgba(128,128,128,0.2); border: 1px solid rgba(128,128,128,0.2); position: relative; flex-shrink: 0; transition: all 0.3s; }
        .sb-pill--on { background: linear-gradient(135deg, #198754 0%, #20c997 100%); border-color: transparent; }
        .sb-pill-thumb { position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.3); transition: transform 0.25s cubic-bezier(0.34,1.56,0.64,1); }
        .sb-pill--on .sb-pill-thumb { transform: translateX(14px); }
        .sb-logout { display: flex; align-items: center; gap: 10px; padding: 9px 10px; border-radius: 11px; border: 1px solid; cursor: pointer; transition: all 0.2s; min-height: 44px; }
        .sb-logout:hover { background: rgba(220,53,69,0.08) !important; border-color: rgba(220,53,69,0.25) !important; }
        .sb-logout--loading { opacity: 0.75; cursor: not-allowed; pointer-events: none; }
        .sb-logout:hover .sb-icon-shell--logout { color: #dc3545 !important; background: rgba(220,53,69,0.1) !important; border-color: rgba(220,53,69,0.22) !important; }
        .sb-logout:hover .sb-label { color: #dc3545 !important; }
        .sb-spinner { animation: sb-spin 0.8s linear infinite; }
        @keyframes sb-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .sb-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); backdrop-filter: blur(4px); z-index: 999; }
        @media (max-width: 1024px) { .sb { left: 0; top: 0; bottom: 0; border-radius: 0 18px 18px 0; width: 252px !important; transform: translateX(-100%); transition: transform 0.28s cubic-bezier(0.4,0,0.2,1); } .sb:not(.sb--collapsed) { transform: translateX(0); } }
      `}</style>
    </>
  );
};

export default Sidebar;